import React from 'react';
import PropTypes from 'prop-types';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import Hyperlink from '../Hyperlink';
import './Footer.css';

/**
 * Footer Component
 *
 * Site-wide footer with link columns, social icons, and legal bar.
 * Responsive: mobile-first with desktop layout at ≥ 1024px.
 *
 * Figma node: 3787:1310 (Footer/Clothing)
 * Desktop: 3787:1309 — 3 columns side by side
 * Mobile: 3848:19879 — columns stacked vertically
 *
 * Appearance follows the data, not the brand: a `logo` (+ `tagline`) switches
 * on the brand block above the columns, `paymentMethods` the badges in the
 * bottom bar, `contact` the contact column — on any brand (OVER-LL
 * 2497:94222 / 2645:13540 is the brand that uses them all). The brand key
 * only picks the skin tokens (`--footer-*`) and the default copy. Brand
 * content that leads somewhere (routes, addresses) belongs in the brand's
 * `Common/Footer` wrapper, not in these defaults.
 *
 * `variant="bar"` renders the legal bar alone (links + badges + copyright) —
 * the cart's mini-footer (OVER-LL cart 2762:29846).
 */

const DEFAULT_CONTENT = {
  CLS: {
    columns: [
      {
        title: 'NEED HELP?',
        links: [
          { label: 'Customer Service', href: '#' },
          { label: 'Size Guide', href: '#' },
          { label: 'FAQ', href: '#' },
        ],
      },
      {
        title: 'C.L. SEIFERT',
        links: [
          { label: 'Studieturtrøje', href: '#' },
          { label: 'Studenterhue', href: '#' },
          { label: 'Skoletøj', href: '#' },
        ],
      },
    ],
    contact: {
      title: 'CONTACT US',
      email: { label: 'info@clseifert.com', href: 'mailto:info@clseifert.com' },
      address: ['Lyngbyvej 20, 4. sal,', 'DK-2100 København Ø, Denmark'],
    },
    socials: [
      { icon: 'facebook', href: 'https://facebook.com', ariaLabel: 'Facebook' },
      { icon: 'instagram', href: 'https://instagram.com', ariaLabel: 'Instagram' },
      { icon: 'youtube', href: 'https://youtube.com', ariaLabel: 'YouTube' },
    ],
    legalLinks: [
      { label: 'Handelsbetingelser', href: '#' },
      { label: 'Privatlivspolitik', href: '#' },
    ],
    copyright: '© 2026 C.L. Seifert A/S, Danmark. Alle rettigheder forbeholdes.',
  },
  ABC: {
    columns: [
      {
        title: 'NEED HELP?',
        links: [
          { label: 'Customer Service', href: '#' },
          { label: 'Size Guide', href: '#' },
          { label: 'FAQ', href: '#' },
        ],
      },
      {
        title: 'ABC-GRUPPEN',
        links: [
          { label: 'Skolmerch', href: '#' },
          { label: 'Overalls & More', href: '#' },
          { label: 'Student Club', href: '#' },
          { label: 'Studentmössor', href: '#' },
        ],
      },
    ],
    contact: {
      title: 'CONTACT US',
      email: { label: 'kundservice@abcgruppen.se', href: 'mailto:kundservice@abcgruppen.se' },
      address: ['Sibyllegatan 47, 1 tr,', 'SE-114 42 Stockholm, Sverige'],
    },
    socials: [
      { icon: 'facebook', href: 'https://facebook.com', ariaLabel: 'Facebook' },
      { icon: 'instagram', href: 'https://instagram.com', ariaLabel: 'Instagram' },
      { icon: 'youtube', href: 'https://youtube.com', ariaLabel: 'YouTube' },
    ],
    legalLinks: [
      { label: 'Köpvillkor', href: '#' },
      { label: 'Personuppgiftspolicy', href: '#' },
    ],
    copyright: '© 2026 ABC-Gruppen, Sverige, All Rights Reserved',
  },
  Overall: {
    // Structural minimum — the wordmark, tagline, columns, contact and payment
    // badges come from Overall/Common/Footer.jsx (page config, CMS-shaped)
    socials: [
      { icon: 'facebook', href: 'https://facebook.com', ariaLabel: 'Facebook' },
      { icon: 'instagram', href: 'https://instagram.com', ariaLabel: 'Instagram' },
      { icon: 'youtube', href: 'https://youtube.com', ariaLabel: 'YouTube' },
    ],
    legalLinks: [
      { label: 'Köpvillkor', href: '#' },
      { label: 'Personuppgiftspolicy', href: '#' },
    ],
    copyright: '© 2026 OVER/LL Studios, Stockholm. All rights reserved.',
  },
};

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

const Footer = ({
  brand = 'CLS',
  columns,
  contact,
  socials,
  legalLinks,
  copyright,
  copyrightPlacement = 'bar',
  logo,
  tagline,
  paymentMethods,
  paymentMethodsLabel = 'Payment methods',
  variant = 'full',
  className = '',
}) => {
  const content = DEFAULT_CONTENT[brand] || DEFAULT_CONTENT.CLS;
  const footerColumns = columns || content.columns || [];
  // `contact={null}` means "no contact column" even where the brand has a default
  const footerContact = contact === undefined ? content.contact : contact;
  const footerSocials = socials || content.socials || [];
  const footerLegalLinks = legalLinks || content.legalLinks || [];
  const footerCopyright = copyright || content.copyright;
  const footerLogo = logo || content.logo;
  const footerTagline = tagline || content.tagline;
  const footerPayments = paymentMethods || content.paymentMethods || [];

  const rootClasses = [
    'ds-footer',
    `ds-footer--${brand.toLowerCase()}`,
    footerLogo && 'ds-footer--has-brand-block',
    footerPayments.length > 0 && 'ds-footer--has-payments',
    variant === 'bar' && 'ds-footer--bar',
    className,
  ].filter(Boolean).join(' ');

  const renderSocials = () => footerSocials.length > 0 && (
    <div className="ds-footer__socials">
      {footerSocials.map((social) => {
        const IconComponent = SOCIAL_ICONS[social.icon];
        // A built-in name renders its lucide glyph; anything else is an image URL (CMS)
        if (!IconComponent && !social.icon) return null;
        return (
          <a
            key={social.icon}
            href={social.href}
            className="ds-footer__social-btn"
            aria-label={social.ariaLabel}
            target="_blank"
            rel="noopener noreferrer"
          >
            {IconComponent ? <IconComponent size={20} /> : <img src={social.icon} alt="" className="ds-footer__social-icon" />}
          </a>
        );
      })}
    </div>
  );

  return (
    <footer className={rootClasses}>
      {/* Main Content Area — not in the bar variant (only the legal bar) */}
      {variant !== 'bar' && (
      <div className="ds-footer__container">
        <div className="ds-footer__content">
          {/* Brand block — wordmark + tagline (Overall) */}
          {footerLogo && (
            <div className="ds-footer__brand">
              <img className="ds-footer__logo" src={footerLogo.src} alt={footerLogo.alt || ''} />
              {footerTagline && <p className="ds-footer__tagline typo-body-lg">{footerTagline}</p>}
            </div>
          )}

          <div className="ds-footer__row">
            {/* Link Columns */}
            {footerColumns.map((col) => (
              <div key={col.title} className="ds-footer__column">
                <h5 className="ds-footer__column-title typo-h5">{col.title}</h5>
                <div className="ds-footer__links">
                  {col.links.map((link) => (
                    <Hyperlink
                      key={link.label}
                      href={link.href || '#'}
                      onClick={link.onClick}
                      variant="regular"
                      className="ds-footer__link"
                    >
                      {link.label}
                    </Hyperlink>
                  ))}
                </div>
              </div>
            ))}

            {/* Contact column (when given) — the socials sit under it, or in
                their own column without one */}
            {footerContact ? (
              <div className="ds-footer__column">
                <h5 className="ds-footer__column-title typo-h5">{footerContact.title}</h5>
                <div className="ds-footer__links">
                  <Hyperlink
                    href={footerContact.email.href}
                    onClick={footerContact.email.onClick}
                    variant="regular"
                    className="ds-footer__link"
                  >
                    {footerContact.email.label}
                  </Hyperlink>
                  <p className="ds-footer__address typo-body">
                    {footerContact.address.map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < footerContact.address.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                {renderSocials()}
              </div>
            ) : (
              footerSocials.length > 0 && (
                <div className="ds-footer__column ds-footer__column--socials">
                  {renderSocials()}
                </div>
              )
            )}
          </div>
        </div>
      </div>
      )}

      {/* Bottom Legal Bar */}
      <div className="ds-footer__bottom">
        <div className="ds-footer__bottom-inner">
          <div className="ds-footer__legal-links">
            {footerLegalLinks.map((link) => (
              <Hyperlink
                key={link.label}
                href={link.href || '#'}
                onClick={link.onClick}
                variant="small"
                className="ds-footer__legal-link"
              >
                {link.label}
              </Hyperlink>
            ))}
          </div>

          {/* Payment badges — when given */}
          {footerPayments.length > 0 && (
            <ul className="ds-footer__payments" aria-label={paymentMethodsLabel}>
              {footerPayments.map(({ id, label, icon }) => (
                <li key={id} className="ds-footer__payment">
                  {icon && <img className="ds-footer__payment-icon" src={icon} alt="" />}
                  <span className="ds-footer__payment-label typo-body-sm">{label}</span>
                </li>
              ))}
            </ul>
          )}

          {footerCopyright && (
            <p className={`ds-footer__copyright typo-body-sm${copyrightPlacement === 'mobile-only' ? ' ds-footer__copyright--mobile-only' : ''}`}>
              {footerCopyright}
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  /** Brand determines default content */
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall']),
  /** Override default link columns */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      links: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          href: PropTypes.string,
          onClick: PropTypes.func,
        }),
      ).isRequired,
    }),
  ),
  /** Contact column; `null` renders none (the socials then take their own column) */
  contact: PropTypes.shape({
    title: PropTypes.string.isRequired,
    email: PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }).isRequired,
    address: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  /** Social links — `icon` is a built-in name (facebook | instagram | youtube) or an image URL */
  socials: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      ariaLabel: PropTypes.string,
    }),
  ),
  /** Legal (bottom bar) links */
  legalLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
  /** Copyright text */
  copyright: PropTypes.string,
  /** Where the copyright shows: in the bottom bar (default) or on mobile only (a brand whose desktop bar is links + badges) */
  copyrightPlacement: PropTypes.oneOf(['bar', 'mobile-only']),
  /** Large wordmark above the columns — switches the brand block on */
  logo: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
  }),
  /** One line under the wordmark */
  tagline: PropTypes.string,
  /** Payment badges in the bottom bar: [{ id, label, icon }] — switches the badges on */
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    }),
  ),
  /** Accessible name of the payment badge list */
  paymentMethodsLabel: PropTypes.string,
  /** `full` = columns + legal bar; `bar` = the legal bar alone (links, badges, copyright) — e.g. under the cart */
  variant: PropTypes.oneOf(['full', 'bar']),
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default Footer;
